import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { ApiService, type Blueprint } from '../../client';
import type { EnhancedToolResult, ToolExecutionContext } from '../types';
import { generateVideoDescriptionFromVideoData } from '../video-state';
import { NO_VIDEO_STATE_MESSAGE } from '../video-state/constants';

import { buildReferenceStateGetter } from './reference-video-transcript';
import { Tool } from './Tool';

import { getOrFetchBlueprint } from 'js/src/data/blueprints/blueprints-operations';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isApiError } from 'js/src/types';

export const fetchReferenceVideoSchema = z.object({
    video_id: z
        .number()
        .optional()
        .describe(
            'ID of the video to use as the reference. Use when the user points at a specific video rather than a blueprint. Do not pass together with blueprint_id.',
        ),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            'The blueprint whose reference video to fetch. When the user has selected a blueprint, this is provided in their preferences. Do not pass together with video_id.',
        ),
});
export type FetchReferenceVideoArgs = z.infer<typeof fetchReferenceVideoSchema>;

const REFERENCE_VIDEO_HEADER =
    '## Reference Video State\n' +
    '_This is the reference video — the video to imitate. It is NOT the video you are editing._\n\n';

function formatBlueprintSettings(blueprint: Blueprint): string {
    const lines: string[] = [
        `- Default template: ${blueprint.default_template_id}`,
        `- Default aspect ratio: ${blueprint.default_aspect_ratio}`,
        `- Narrative thread: ${blueprint.narrative_thread}`,
        `- Target duration: ${blueprint.default_target_duration_seconds} seconds`,
    ];
    if (blueprint.ai_voice) {
        lines.push(`- AI voice: ${blueprint.ai_voice}`);
    }
    if (blueprint.description) {
        lines.push(`- Description: ${blueprint.description}`);
    }
    if (blueprint.supported_templates.length > 0) {
        lines.push(
            `- Templates with reference examples: ${blueprint.supported_templates.join(', ')}`,
        );
    }
    return lines.join('\n');
}

/**
 * Fetches a reference video — either a blueprint's example video or any video
 * the user can access — and returns its full annotation, so the agent can
 * build a new video that matches the reference's script style and visual
 * choices. The blueprint path resolves the example's video id off the
 * blueprint and then fetches it like any other, additionally returning the
 * blueprint's settings and custom instructions.
 */
export class FetchReferenceVideoTool extends Tool<typeof fetchReferenceVideoSchema> {
    protected override readonly schema = fetchReferenceVideoSchema;
    protected override readonly toolName = TOOL_NAMES.fetchReferenceVideo;
    protected override readonly toolDescription =
        "Fetch a reference video's complete scene-by-scene annotation (script text, scene designs, media, durations) to imitate its style. Pass blueprint_id when the user selected a blueprint (also returns the blueprint's settings and custom instructions); pass video_id to reference one of the user's own videos directly. Call this BEFORE building a video that should match an existing video's style.";

    public override getToolDisplayName(): string {
        return 'Studying reference video';
    }

    public get statusIcon(): string {
        return 'video_library';
    }

    public async execute(
        args: FetchReferenceVideoArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { video_id: videoId, blueprint_id: blueprintId } = args;

        if (videoId && blueprintId) {
            return {
                result: 'Error: Pass either video_id or blueprint_id, not both.',
                isSuccess: false,
            };
        }
        if (videoId) {
            return this.fetchByVideoId(videoId);
        }
        if (blueprintId) {
            return this.fetchByBlueprintId(blueprintId);
        }
        return {
            result: 'Error: Either video_id or blueprint_id is required.',
            isSuccess: false,
        };
    }

    /**
     * Serializes the reference video into its annotation, or null when the
     * video has no scenes (the serializer's "no video yet" message describes
     * the agent's own video, which is exactly what a reference must not be
     * confused with).
     */
    private async describeReference(
        videoData: OpaqueVideoData,
        transcripts?: Record<string, Record<string, unknown>>,
    ): Promise<string | null> {
        // Overlay the reference video's transcript editors onto the session state so its narration
        // renders with per-scene coverage and pauses — identical to get_video_state.
        const getState = buildReferenceStateGetter(this.getState(), transcripts);
        const annotation = await generateVideoDescriptionFromVideoData(videoData, {
            header: REFERENCE_VIDEO_HEADER,
            getState,
        });
        return annotation === NO_VIDEO_STATE_MESSAGE ? null : annotation;
    }

    private async fetchAndDescribe(
        videoId: number,
    ): Promise<{ name: string; annotation: string | null }> {
        const videoResponse = await ApiService.videoRetrieve({
            path: { video_id: String(videoId) },
        });
        const video = videoResponse.data;

        return {
            name: video.name,
            annotation: await this.describeReference(
                video.video_data as OpaqueVideoData,
                video.transcripts,
            ),
        };
    }

    private async fetchByVideoId(videoId: number): Promise<EnhancedToolResult> {
        const { name, annotation } = await this.fetchAndDescribe(videoId);
        if (annotation === null) {
            return {
                result: `Error: Reference video "${name}" (id ${videoId}) has no scenes to imitate. Ask the user for a different reference video, or build without one.`,
                isSuccess: false,
            };
        }

        return {
            result: `Success. Fetched reference video "${name}" (id ${videoId}).

${annotation}`,
            isSuccess: true,
        };
    }

    private async fetchByBlueprintId(blueprintId: number): Promise<EnhancedToolResult> {
        const blueprint = await this.dispatch(getOrFetchBlueprint(blueprintId));
        if (!blueprint) {
            return {
                result: `Error: Blueprint ${blueprintId} was not found or is not accessible.`,
                isSuccess: false,
            };
        }

        // The agent imitates the example's style, not its template, so any of the blueprint's
        // examples will do — take the default one, falling back to whichever exists.
        const exampleVideoId =
            blueprint.supported_templates_data[blueprint.default_template_id]?.video_id ??
            Object.values(blueprint.supported_templates_data)[0]?.video_id;
        if (exampleVideoId === undefined) {
            return {
                result: `Error: Blueprint ${blueprintId} ("${blueprint.name}") has no reference video. Ask the user for a different reference`,
                isSuccess: false,
            };
        }

        let referenceAnnotation: string | null;
        try {
            ({ annotation: referenceAnnotation } = await this.fetchAndDescribe(exampleVideoId));
        } catch (error) {
            // A public blueprint's example can live in another workspace, which this fetch has no
            // grant for. That combination isn't expected to reach this tool, so report it rather
            // than papering over it — a hit here means the assumption no longer holds.
            if (isApiError(error) && (error.status === 403 || error.status === 404)) {
                ErrorReporter.capture(error, {
                    fingerprint: ['fetch-reference-video-blueprint-example-denied'],
                    extra: {
                        blueprintId,
                        exampleVideoId,
                        isPublicBlueprint: blueprint.is_public,
                    },
                });
                return {
                    result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) could not be read. Ask the user for a different reference.`,
                    isSuccess: false,
                };
            }
            throw error;
        }

        if (referenceAnnotation === null) {
            return {
                result: `Error: The reference video for blueprint "${blueprint.name}" (id ${blueprintId}) has no scenes to imitate. Ask the user for a different reference.`,
                isSuccess: false,
            };
        }

        const customInstructionsSection = blueprint.custom_instructions
            ? `\n\n## Custom Instructions (follow these)\n${blueprint.custom_instructions}`
            : '';

        return {
            result: `Success. Fetched the reference video for blueprint "${blueprint.name}" (id ${blueprintId}).

## Blueprint Settings
${formatBlueprintSettings(blueprint)}${customInstructionsSection}

${referenceAnnotation}`,
            isSuccess: true,
        };
    }
}
